import { useCallback, useMemo, useState } from 'react';
import { useClient } from 'urql';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { hasPermission } from '@/utils/permissions.js';
import {
  createMantenimientoFlota,
  deleteMantenimientoFlota,
  fetchMantenimientoFlota,
  fetchMantenimientosFlota,
  updateMantenimientoFlota,
} from '../services/mantenimientoFlota.service.js';

export const DEFAULT_PAGE_SIZE = 10;

export function useMantenimientoFlota() {
  const client = useClient();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const permissions = useMemo(() => ({
    canView: hasPermission(user, 'mantenimiento_flota.ver'),
    canCreate: hasPermission(user, 'mantenimiento_flota.crear'),
    canEdit: hasPermission(user, 'mantenimiento_flota.editar'),
    canDelete: hasPermission(user, 'mantenimiento_flota.eliminar'),
  }), [user]);

  const list = useCallback(async ({ page, pageSize, filter }) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMantenimientosFlota({ page, pageSize, filter }, client);
      return data;
    } catch (err) {
      setError(err.message || 'No se pudo obtener el listado.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  const getById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      return await fetchMantenimientoFlota(id, client);
    } catch (err) {
      setError(err.message || 'No se pudo obtener el mantenimiento.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  const create = useCallback(async (input) => {
    setLoading(true);
    setError(null);
    try {
      return await createMantenimientoFlota(input, client);
    } catch (err) {
      setError(err.message || 'No se pudo crear el mantenimiento.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  const update = useCallback(async (id, input) => {
    setLoading(true);
    setError(null);
    try {
      return await updateMantenimientoFlota(id, input, client);
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el mantenimiento.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  const remove = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      return await deleteMantenimientoFlota(id, client);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el mantenimiento.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return {
    loading,
    error,
    permissions,
    list,
    getById,
    create,
    update,
    remove,
  };
}
